import type { CoordinateConfig } from '@k-ucap/shared';
import type { RandomGenerator } from './SeededRandom.js';
import { defaultRandom } from './SeededRandom.js';

const EARTH_RADIUS_KM = 6371;

const degToRad = (deg: number) => (deg * Math.PI) / 180;
const radToDeg = (rad: number) => (rad * 180) / Math.PI;

export interface LatLonAlt {
  latitude: number;
  longitude: number;
  altitudeM?: number;
}

export const generateRandomCoordinate = (
  config: CoordinateConfig,
  random: RandomGenerator = defaultRandom
): LatLonAlt => {
  if (config.mode === 'FIXED') {
    return {
      latitude: config.centerLat ?? 0,
      longitude: config.centerLon ?? 0,
      altitudeM: 0,
    };
  }

  if (config.mode === 'BOUNDING_BOX') {
    return {
      latitude: random.nextRange(config.minLat ?? -90, config.maxLat ?? 90),
      longitude: random.nextRange(config.minLon ?? -180, config.maxLon ?? 180),
      altitudeM: random.nextRange(100, 3000),
    };
  }

  if (config.mode === 'CENTER_RADIUS') {
    const centerLat = config.centerLat ?? 0;
    const centerLon = config.centerLon ?? 0;
    const radiusKm = config.radiusKm ?? 1;

    const u = random.next();
    const v = random.next();
    const w = radiusKm / EARTH_RADIUS_KM;
    const c = 2 * Math.PI * v;
    const r = Math.sqrt(u) * w;
    const latRad = Math.asin(
      Math.sin(degToRad(centerLat)) * Math.cos(r) + Math.cos(degToRad(centerLat)) * Math.sin(r) * Math.cos(c)
    );
    const lonRad =
      degToRad(centerLon) +
      Math.atan2(
        Math.sin(c) * Math.sin(r) * Math.cos(degToRad(centerLat)),
        Math.cos(r) - Math.sin(degToRad(centerLat)) * Math.sin(latRad)
      );
    return {
      latitude: radToDeg(latRad),
      longitude: radToDeg(lonRad),
      altitudeM: random.nextRange(100, 3000),
    };
  }

  if (config.mode === 'ROUTE') {
    // Route-based random coordinate: for now, return center
    return {
      latitude: config.centerLat ?? 0,
      longitude: config.centerLon ?? 0,
      altitudeM: 0,
    };
  }

  return { latitude: 0, longitude: 0, altitudeM: 0 };
};

export const generateRandomNumber = (min: number, max: number, random: RandomGenerator = defaultRandom): number =>
  random.nextRange(min, max);

export const generateRandomEnum = (values: string[], random: RandomGenerator = defaultRandom): string =>
  random.nextItem(values);

export const generateRandomBoolean = (random: RandomGenerator = defaultRandom): boolean => random.nextBoolean();

export const RandomCoordinateService = {
  generateRandomCoordinate,
  generateRandomNumber,
  generateRandomEnum,
  generateRandomBoolean,
};

export default RandomCoordinateService;
